import { useState, useEffect, useCallback } from 'react';
import { Sun, Moon } from 'lucide-react';
import './ThemeToggle.css';

/**
 * Theme control.
 *
 * Defaults to the visitor's system preference and only overrides it once
 * they have made an explicit choice, which is then remembered.
 */
function ThemeToggle() {
  const [theme, setTheme] = useState('dark');

  /* Dark is the default by explicit request. Light remains fully
     supported and is one click away; a saved choice always wins. */
  useEffect(() => {
    const saved = localStorage.getItem('theme');
    const initial = saved === 'dark' || saved === 'light' ? saved : 'dark';

    setTheme(initial);
    document.documentElement.setAttribute('data-theme', initial);
  }, []);

  const toggle = useCallback(() => {
    setTheme((current) => {
      const next = current === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('theme', next);
      return next;
    });
  }, []);

  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      className="theme-toggle"
      onClick={toggle}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark
        ? <Moon size={15} strokeWidth={1.5} aria-hidden="true" />
        : <Sun size={15} strokeWidth={1.5} aria-hidden="true" />}
    </button>
  );
}

export default ThemeToggle;
